import { UpdateItemDto } from '@nohara-dxdev/mt-common';
import { ApiResponse } from '@nohara-dxdev/oidc-core';
import { MutationConfig, useNotifications } from '@nohara-dxdev/react-core';
import { useScopeIds } from '@nohara-dxdev/react-oidc';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { api } from '@/lib/api-client';

import { getItemQueryKey } from './get-item';
import { getItemInfrastructuresQueryKey } from './get-item-infrastructures';
import { getItemsQueryKey } from './get-items';

export const updateItem = async (
  orgId: string,
  projectId: string,
  itemId: string,
  item: UpdateItemDto,
): Promise<{ success: boolean }> => {
  const { data } = await api.patch<unknown, ApiResponse<{ success: boolean }>>(
    `/orgs/${orgId}/projects/${projectId}/items/${itemId}`,
    item,
  );

  return data;
};

export interface UseUpdateItemOptions {
  mutationConfig?: MutationConfig<(data: UpdateItemDto) => Promise<{ success: boolean }>>;
}

export const useUpdateItem = (itemId: string, { mutationConfig }: UseUpdateItemOptions = {}) => {
  const queryClient = useQueryClient();

  const notifications = useNotifications();
  const { orgId, projectId } = useScopeIds('org', 'project');

  const { onSuccess, onError, ...restConfig } = mutationConfig ?? {};

  return useMutation({
    onSuccess: (_, variables, ...args) => {
      // Invalidate items list
      void queryClient.invalidateQueries({ queryKey: getItemsQueryKey(orgId, projectId) });

      // Invalidate individual item query
      void queryClient.invalidateQueries({
        queryKey: getItemQueryKey(orgId, projectId, itemId),
      });

      // Invalidate item infrastructures
      void queryClient.invalidateQueries({
        queryKey: getItemInfrastructuresQueryKey(orgId, projectId, itemId),
      });

      notifications.success('items.update.success');
      onSuccess?.(_, variables, ...args);
    },
    onError: (...args) => {
      onError?.(...args);
      notifications.error('items.update.error');
    },
    ...restConfig,
    mutationFn: (data: UpdateItemDto) => updateItem(orgId, projectId, itemId, data),
  });
};
